import React, { useEffect, useRef, useState } from 'react';
import { Camera, X } from 'lucide-react';

/** Reads a QR from a photo with jsQR, trying a few sizes (a phone photo is often 4000 px wide). */
async function jsQrFromFile(file: File): Promise<string> {
  const { default: jsQR } = await import('jsqr');
  const img = await createImageBitmap(file);
  const longest = Math.max(img.width, img.height);
  for (const target of [1000, 700, 1400, longest]) {
    const k = Math.min(1, target / longest);
    const w = Math.round(img.width * k);
    const h = Math.round(img.height * k);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const g = canvas.getContext('2d', { willReadFrequently: true })!;
    g.fillStyle = '#fff';
    g.fillRect(0, 0, w, h);
    g.drawImage(img, 0, 0, w, h);
    const code = jsQR(g.getImageData(0, 0, w, h).data, w, h, { inversionAttempts: 'attemptBoth' });
    if (code?.data) return code.data;
  }
  throw new Error('No QR code found');
}

const SCAN_EL = 'agent-qr-reader';
const FILE_EL = 'agent-qr-file';

/**
 * Full-screen camera scanner for the customer's delivery QR. The live camera
 * needs https and the camera permission; "Take a photo of the QR" works as a
 * fallback on phones where the live view won't start.
 */
export function QrScanner({ title, hint, onResult, onClose }: { title: string; hint?: string; onResult: (text: string) => void; onClose: () => void }) {
  const [typed, setTyped] = useState('');
  const scanner = useRef<import('html5-qrcode').Html5Qrcode | null>(null);
  const done = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        if (cancelled) return;
        // The phone's own QR reader (Android Chrome) where there is one - faster and surer than ZXing.
        const s = new Html5Qrcode(SCAN_EL, { verbose: false, experimentalFeatures: { useBarCodeDetectorIfSupported: true } });
        scanner.current = s;
        const found = (text: string) => {
          if (done.current) return;
          done.current = true;
          s.stop().catch(() => {}).finally(() => onResult(text));
        };
        // A wide box: square QR codes and long barcodes both fit.
        const box = (w: number, h: number) => ({ width: Math.floor(Math.min(w * 0.9, 340)), height: Math.floor(Math.min(h * 0.7, 240)) });
        await s.start({ facingMode: 'environment' }, { fps: 10, qrbox: box }, found, () => {})
          .catch(() => s.start({ facingMode: 'user' }, { fps: 10, qrbox: box }, found, () => {}));
      } catch (err) {
        const name = err instanceof Error ? err.name : '';
        setError(name === 'NotAllowedError'
          ? 'Camera permission is off. Allow the camera for this site in the browser, or use "Take a photo of the code".'
          : 'The camera could not start. Use "Take a photo of the code" below.');
      }
    })();
    return () => {
      cancelled = true;
      const s = scanner.current;
      if (s?.isScanning) s.stop().catch(() => {});
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fromPhoto = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    if (scanner.current?.isScanning) await scanner.current.stop().catch(() => {});
    try {
      // ZXing (html5-qrcode) misses some codes that jsQR reads, and the other way round - try both.
      const text = await jsQrFromFile(file).catch(async () => {
        const { Html5Qrcode } = await import('html5-qrcode');
        return new Html5Qrcode(FILE_EL).scanFile(file, false);
      });
      done.current = true;
      onResult(text);
    } catch (err) {
      console.warn('QR photo scan failed', err);
      setError('No QR code or barcode found in that photo. Hold the phone steady and closer, then try again.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black text-white">
      <div className="flex items-center gap-3 p-4">
        <p className="flex-1 truncate font-semibold">{title}</p>
        <button type="button" onClick={onClose} className="rounded-full bg-white/15 p-2" aria-label="Close"><X className="h-5 w-5" /></button>
      </div>
      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4">
        <div id={SCAN_EL} className="w-full max-w-sm overflow-hidden rounded-xl bg-black" />
        {/* Photo decoding draws into this box - it needs a real size, so it sits off-screen rather than hidden. */}
        <div id={FILE_EL} aria-hidden style={{ position: 'fixed', left: -10000, top: 0, width: 800, height: 800 }} />
        <p className="text-center text-sm text-white/80">{hint ?? 'Point the camera at the QR code or barcode.'}</p>
        {error && <p className="rounded-lg bg-critical-600/90 p-3 text-center text-sm">{error}</p>}
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-white px-4 py-3 font-semibold text-brand-green-900">
          <Camera className="h-5 w-5" /> Take a photo of the code
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => fromPhoto(e.target.files?.[0])} />
        </label>
        {/* A barcode that won't scan can be typed (the number printed under it). */}
        <form className="flex w-full max-w-sm gap-2" onSubmit={(e) => { e.preventDefault(); if (typed.trim()) { done.current = true; onResult(typed.trim()); } }}>
          <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Or type the number under the barcode"
            className="min-w-0 flex-1 rounded-xl px-3 py-2.5 text-sm text-ink-900" />
          <button type="submit" disabled={!typed.trim()} className="rounded-xl bg-brand-green-600 px-4 text-sm font-bold disabled:opacity-50">OK</button>
        </form>
      </div>
    </div>
  );
}
